import React, { useState, useEffect } from "react";

import { Col, Row } from "react-bootstrap";
import Axios from "axios";
import DatePicker from "react-datepicker";
import JSZip from "jszip";
import MaterialTable from "material-table";
import moment from "moment-timezone";

import { useFormatter } from "../../../util/formatter";
import { useSaving, useSolarCalc } from "../../../util/calculations";
import { useUser } from "../../../util/user";
import GYAxios from "../../../util/GYAxios";
import Noti from "../../../util/noti";
import usePeakLabels from "../../../util/peakLabels";

import "../../../styles/general.css";
import { localTimeToPickerTime, pickerTimeTolocalTime } from "../../../util/momentTimezone";

function SummaryReport(props) {
	window.JSZip = JSZip;
	const user = useUser();
	const [siteSet, setSiteSet] = useState("");
	const [country, setCountry] = useState("");
	const [isReady, setIsReady] = useState(false);
	const [columns, setColumns] = useState([]);
	const [rows, setRows] = useState([]);
	const [formatNumber] = useFormatter();
	const [selectedMonth, setSelectedMonth] = useState(
		moment().subtract(1, "month")
	);
	const [yearMonth, setYearMonth] = useState(selectedMonth.format("MMMM YYYY"));
	const [selectedYear, setSelectedYear] = useState(
		selectedMonth.format("YYYY")
	);
	const minDate = moment("2021-01-01", "YYYY-MM-DD").toDate();
	const maxDate = moment().subtract(1, "month").endOf("month").toDate();

	const [isDisabled, setIsDisabled] = useState(null);
	const solarCalc = useSolarCalc();
	const peakLabels = usePeakLabels(country);

	const savingData = useSaving({month: selectedMonth});

	/**
	 * Format summary sites array
	 * @param {Summary Sites} summarySites
	 * @returns array - formatted summary sites
	 */
	const buildTableOpts = (summarySites) => {
		const width = 150;

		const THColumns = [
			{ title: "GY Entity", field: "entity_id", width },
			{ title: "Client", field: "company", width },
			{ title: "Site Name", field: "site_name", width },
			{ title: "Site Code", field: "site_code", width },
			{ title: "Project ID", field: "project_id", width },
			{ title: "Invoice Number", field: "invoice_no", width },
			{ title: "Invoice Date", field: "invoice_date", width },
			{ title: "Invoice Due Date", field: "invoice_due_date", width },
			{ title: `Basic Tariff ${peakLabels[0]}`, field: "tariff_ON", width },
			{ title: `Basic Tariff ${peakLabels[1]}`, field: "tariff_OFF", width },
			{ title: "FT Cost", field: "ft_cost", width },
			{ title: `Discount ${peakLabels[0]} (%)`, field: "discount_ON", width },
			{ title: `Discount ${peakLabels[1]} (%)`, field: "discount_OFF", width },
			{ title: "FT Discount (%)", field: "ft_discount", width },
			{ title: "FT (after) Discount", field: "fta_discount", width },
			{ title: `Energy deduction ${peakLabels[0]} (kWh)`, field: "energy_deduction_ON", width },
			{ title: `Prod ${peakLabels[0]} (kWh)`, field: "solar_ON", width },
			{ title: `Unit Cost ${peakLabels[0]}`, field: "unit_cost_ON", width },
			{ title: `Sub total ${peakLabels[0]}`, field: "sub_ON", width },
			{ title: `Energy deduction ${peakLabels[1]} (kWh)`, field: "energy_deduction_OFF", width },
			{ title: `Prod ${peakLabels[1]} (kWh)`, field: "solar_OFF", width },
			{ title: `Unit Cost ${peakLabels[1]}`, field: "unit_cost_OFF", width },
			{ title: `Sub total ${peakLabels[1]}`, field: "sub_OFF", width },
			{ title: "Total energy deduction (kWh)", field: "energy_deduction_total", width },
			{ title: "Total solar prod (kWh)", field: "solar_total", width },
			{ title: "Unit FT Cost ", field: "unit_FT", width },
			{ title: "Sub total FT", field: "sub_FT", width },
			{ title: "Amount Ex VAT", field: "amount_ex_VAT", width },
			{ title: "VAT", field: "vat", width },
			{ title: "Total Inc VAT", field: "amount_inc_VAT", width },
			{ title: "Saving amount", field: "savings", width },
			{ title: "BPA Fee", field: "bpa_fee", width },
			{ title: "VAT", field: "vat_percentage", width },
			{ title: "Total", field: "total_percentage", width },
			{ title: "Total Irradiation", field: "irr", width },
			{ title: "Actual PR", field: "brut_PR", width },
			{ title: "Avg Ambient Temp (°C)", field: "ambient_temp_avg", width },
		];

		const VNColumns = [
			{ title: "GY Entity", field: "entity_id", width },
			{ title: "Client", field: "company", width },
			{ title: "Site Name", field: "site_name", width },
			{ title: "Contract", field: "contract_desc", width },
			{ title: "Site Code", field: "site_code", width },
			{ title: "Project ID", field: "project_id", width },
			{ title: "Proforma Invoice Number", field: "proforma_inv_no", width },
			{ title: "Invoice Date", field: "invoice_date", width },
			{ title: "Invoice Due Date", field: "invoice_due_date", width },
			{ title: `Basic Tariff ${peakLabels[0]}`, field: "tariff_ON", width },
			{ title: `Basic Tariff ${peakLabels[1]}`, field: "tariff_OFF", width },
			{ title: `Discount ${peakLabels[0]} (%)`, field: "discount_ON", width },
			{ title: `Discount ${peakLabels[1]} (%)`, field: "discount_OFF", width },
			{ title: `Prod ${peakLabels[0]} (kWh)`, field: "solar_ON", width },
			{ title: `Unit Cost ${peakLabels[0]}`, field: "unit_cost_ON", width },
			{ title: `Sub total ${peakLabels[0]}`, field: "sub_ON", width },
			{ title: `Prod ${peakLabels[1]} (kWh)`, field: "solar_OFF", width },
			{ title: `Unit Cost ${peakLabels[1]}`, field: "unit_cost_OFF", width },
			{ title: `Sub total ${peakLabels[1]}`, field: "sub_OFF", width },
			{ title: "Total solar prod (kWh)", field: "solar_total", width },
			{
				title: `${peakLabels[0]} Delivered (kWh)`,
				field: "del_peak",
				width,
			},
			{
				title: `${peakLabels[1]} Delivered (kWh)`,
				field: "del_std",
				width,
			},
			{ title: "Total Delivered (kWh)", field: "del_total", width },
			{ title: "EVN Tariff Unit Cost", field: "FIT_on", width },
			{ title: "Shared Percentage (%)", field: "shared_percentage", width },
			{ title: "Shared Revenue", field: "shared_rev", width },
			{ title: "Amount Ex VAT", field: "amount_ex_VAT", width },
			{ title: "VAT", field: "vat", width },
			{ title: "Total Inc VAT", field: "amount_inc_VAT", width },
			{ title: "Saving amount", field: "savings", width },
			{ title: "Total Irradiation", field: "irr", width },
			{ title: "Actual PR", field: "brut_PR", width },
			{ title: "Avg Ambient Temp (°C)", field: "ambient_temp_avg", width },
		];

		const columns = country === "VN" ? VNColumns : THColumns;

		const calculateFunction =
			country === "VN" ? calculateVNData : calculateTHData;

		const data = summarySites.map((site) => calculateFunction(site));

		setColumns(columns);
		setRows(data);
		setIsReady(true);
	};

	const mapEnergyDeduction = (monthlyRow) => {
		if(monthlyRow.is_grid_reinject_applied) {
			monthlyRow.solar_ON = monthlyRow.solar_ON - monthlyRow.delivered_ON;
			monthlyRow.solar_OFF = monthlyRow.solar_OFF - monthlyRow.delivered_OFF;
			monthlyRow.solar_total = monthlyRow.solar_total - monthlyRow.delivered_total;
			monthlyRow.energy_deduction_ON = monthlyRow.delivered_ON;
			monthlyRow.energy_deduction_OFF = monthlyRow.delivered_OFF;
			monthlyRow.energy_deduction_total = monthlyRow.delivered_total;
		} else {
			monthlyRow.energy_deduction_ON = 0;
			monthlyRow.energy_deduction_OFF = 0;
			monthlyRow.energy_deduction_total = 0;
		}
		return monthlyRow;
	}

	/**
	 * Fetch data for summary report of site groups(MK1, MK2, CP)
	 * @param {Site group to fetch} siteSet
	 */
	const fetchData = (siteSet) => {
		let clientID = "";
		switch (siteSet) {
			case "MK1":
			case "MK2":
				clientID = 18;
				break;
			case "CP":
				clientID = 21;
				break;
			default:
				break;
		}

		const fromDate = selectedMonth.startOf("month").format("YYYY-MM-DD");
		const endOfMonth = selectedMonth.endOf("month");
		const toDateParam = endOfMonth.format("YYYYMMDD");
		const YYYYMM = moment(fromDate).format("YYYYMM");

		let column = "admin";
		let id = "all";
		let country = "TH";

		// Client selected
		if (clientID !== "") {
			column = "client_id";
			id = clientID;
		}
		// Country selected
		else country = siteSet;

		// COD sites Request
		const codURI = `/site/cod-only/moment/${toDateParam}/filter/${column}/${id}`;
		const reqCOD = GYAxios.get(codURI);

		// Monthly Data Request
		const viewLevel = props.viewLevel;
		const reqMonthlyURI = `/reports/summary/acct/month/${YYYYMM}/country/${country}/level/${viewLevel}`;
		const reqEnergy = GYAxios.get(reqMonthlyURI);

		const reqFreeSites = GYAxios.get("/site/free");
		const reqFITPrice = GYAxios.get(`/reports/price/FIT/${YYYYMM}`);
		const reqEVNInput = GYAxios.get(`/evn_input/month/${YYYYMM}`);

		// Requests Array
		const reqArray = [reqCOD, reqEnergy, reqFreeSites];

		if (country === "VN") {
			reqArray.push(reqFITPrice);
			reqArray.push(reqEVNInput);
		}

		// Make Requests
		Axios.all(reqArray)
			.then(
				Axios.spread((...responses) => {
					const resCOD = responses[0];
					const resEnergy = responses[1];
					const resFreeSites = responses[2];
					const resFITPrice =
						responses[3] != null && responses[3].data.error == null
							? responses[3].data[0]
							: "";
					const resEVNInput =
						responses[4] != null && responses[4].data.error == null
							? responses[4].data
							: "";

					const freeSites =
						resFreeSites.data.error == null
							? resFreeSites.data.map((freeSite) => freeSite.table_name)
							: [];

					let summarySites = [];

					let invoice_date = selectedMonth.endOf("month").format("YYYY-MM-DD");

					// Map energy deduction for each site
					resEnergy.data = resEnergy.data.map(mapEnergyDeduction);

					// Filter by clients
					if (clientID !== "") {
						resCOD.data.map((CODsite, id) => {
							for (let i = 0; i < resEnergy.data.length; i++) {
								const site = resEnergy.data[i];

								// Site is COD and not a free site
								if (
									CODsite.table_name === site.table_name &&
									!freeSites.includes(site.table_name)
								) {
									// Site is a Makro Site
									if (siteSet.includes("MK")) {
										// Site is a MK Phase 1 site
										if (siteSet === "MK1" && site.site_code.length === 5)
											summarySites.push({ ...site, index: id, invoice_date });
										// Site is a MK Phase 2 site
										else if (siteSet === "MK2" && site.site_code.length === 6)
											summarySites.push({ ...site, index: id, invoice_date });
									}
									// Site is a CP ALL site
									else {
										let _invoice_date = site.inv_issued_date == null ? invoice_date : moment(site.inv_issued_date).format("YYYY-MM-DD");
										summarySites.push({ ...site, index: id, invoice_date: _invoice_date });
									}

									break;
								}
							}
						});
					}
					// Filter by countries
					else {
						resCOD.data.map((CODsite, id) => {
							for (let i = 0; i < resEnergy.data.length; i++) {
								const site = resEnergy.data[i];

								// Site is COD
								if (CODsite.table_name === site.table_name && CODsite.operation_status === 3) {
									let _invoice_date = site.inv_issued_date == null ? invoice_date : moment(site.inv_issued_date).format("YYYY-MM-DD");
									summarySites.push({ ...site, index: id, invoice_date: _invoice_date });

									break;
								}
							}
						});

						if (country === "VN")
							summarySites = summarySites.map((site) =>
								handleVNSites(site, resFITPrice, resEVNInput)
							);
					}
					
					buildTableOpts(summarySites);
				})
			)
			.catch((err) => console.log("Request error: ", err));
	};

	/**
	 * Format data with FIT prices and EVN inputs
	 * @param {obj} site
	 * @param {obj} FITPrice
	 * @param {obj} EVNInput
	 * @returns
	 */
	const handleVNSites = (site, FITPrice, EVNInput) => {
		let tempSite = site;
		for (let i = 0; i < EVNInput.length; i++) {
			const evnInput = EVNInput[i];
			if (site.table_name === evnInput.table_name) {
				tempSite = {
					...tempSite,
					FITOn: FITPrice.FITOn,
					FITOff: FITPrice.FITOff,
					evnInput,
				};
				break;
			}
		}
		return tempSite;
	};

	/**
	 * Calculate data of TH sites
	 * @param {obj} site
	 * @returns TH sites data
	 */
	const calculateTHData = (site) => {
		// Special decimal place of 4 for BTG sites
		const isBTGSite = site.client_id === 69;
		const specialDP = isBTGSite ? 4 : null;
		const dec = isBTGSite ? 4 : 6;

		let irr = solarCalc.calculateIrr(site.irradiance_sum).toFixed(2);
		let unit_cost_ON = solarCalc.calculateUnitCost(
			site.price_on,
			site.ON_discount,
			site.currency,
			specialDP
		);
		let unit_cost_OFF = solarCalc.calculateUnitCost(
			site.price_off,
			site.OFF_discount,
			site.currency,
			specialDP
		);
		let unit_FT = solarCalc.calculateUnitCost(
			site.FT_cost,
			site.FT_discount,
			site.currency,
			specialDP
		);

		let prod = {
			ON: site.solar_ON,
			OFF: site.solar_OFF,
			TOTAL: site.solar_total,
		};
		let price = {
			ON: site.price_on,
			OFF: site.price_off,
			FT: site.FT_cost,
		};
		let discount = {
			ON: site.ON_discount,
			OFF: site.OFF_discount,
			FT: site.FT_discount,
		};

		// const savingsObj = solarCalc.calculateSavings(
		// 	prod,
		// 	price,
		// 	discount,
		// 	site.currency
		// );
		// const savings = solarCalc.parseFormatNumber(savingsObj.savingsTotal, 2);
		const savings = savingData.find(saving => saving.table_name === site.table_name)?.saving || 0;
		
		const costsObj = solarCalc.calculateProdCost(
			prod,
			price,
			discount,
			site.currency,
			specialDP
		);

		let sub_ON = costsObj.costON;
		let sub_OFF = costsObj.costOFF;
		let sub_FT = costsObj.costFT;

		let amount_ex_VAT = costsObj.costTotal;

		const vatPercent = site.vat;
		const currency = site.currency;

		const VATObj = solarCalc.calculateVAT(amount_ex_VAT, currency, vatPercent);

		let vat = VATObj.VAT;
		let amount_inc_VAT = VATObj.totalDue;

		let bpa_fee = site.BPA_fee ?? 0;
		const BPA_VATObj = solarCalc.calculateVAT(bpa_fee, currency, vatPercent);
		const discount_ON = formatNumber(parseFloat(site.discount_on * 100), 2);
		const discount_OFF = formatNumber(parseFloat(site.discount_off * 100), 2);
		const fta_discount = solarCalc.calcFTA(site.ft_discount, site.FT_cost);

		const values = {
			entity_id: site.entity_id,
			invoice_no: site.invoice_no ?? "-",
			invoice_date: site.invoice_date,
			invoice_due_date: moment(site.invoice_date, "YYYYMMDD")
				.add(parseFloat(site.credit_term), "day")
				.format("YYYY-MM-DD"),
			tariff_ON: site.price_on ?? '-',
			tariff_OFF: site.price_off ?? '-',
			ft_cost: site.FT_cost ?? '-',
			discount_ON: discount_ON,
			discount_OFF: discount_OFF,
			ft_discount: parseFloat(site.ft_discount * 100, 2) ?? "-",
			fta_discount: fta_discount === null ? "-" : fta_discount.toFixed(2),
			company: site.company,
			project_id: site.SF_project_id ?? '-',
			site_code: site.site_code,
			site_name: site.site_name,
			contract_desc: site.contract_desc,
			energy_deduction_ON: site.energy_deduction_ON ? formatNumber(site.energy_deduction_ON, 3) : "-",
			solar_ON: formatNumber(site.solar_ON, 3),
			unit_cost_ON: unit_cost_ON.toFixed(dec),
			sub_ON: formatNumber(sub_ON.toFixed(2), 2),
			energy_deduction_OFF: site.energy_deduction_OFF ? formatNumber(site.energy_deduction_OFF, 3) : "-",
			solar_OFF: formatNumber(site.solar_OFF, 3),
			unit_cost_OFF: unit_cost_OFF.toFixed(dec),
			sub_OFF: formatNumber(sub_OFF.toFixed(2), 2),
			energy_deduction_total: site.energy_deduction_total ? formatNumber(site.energy_deduction_total, 3) : "-",
			solar_total: formatNumber(site.solar_total, 3),
			unit_FT: unit_FT.toFixed(dec),
			sub_FT: formatNumber(sub_FT.toFixed(2), 2),
			amount_ex_VAT: formatNumber(amount_ex_VAT.toFixed(2), 2),
			vat: formatNumber(vat.toFixed(2), 2),
			amount_inc_VAT: formatNumber(amount_inc_VAT.toFixed(2), 2),
			savings: formatNumber(savings, 2),
			bpa_fee: formatNumber(bpa_fee.toFixed(2), 2),
			vat_percentage: formatNumber(BPA_VATObj.VAT.toFixed(2), 2),
			total_percentage: formatNumber(BPA_VATObj.totalDue.toFixed(2), 2),
			irr,
			brut_PR: site.brut_PR,
			ambient_temp_avg: formatNumber(site.ambient_temp_avg, 2),
		};
		return values;
	};

	/**
	 * Calculate base VN data
	 * @param {obj} site
	 * @returns
	 */
	const calculateVNData = (site) => {
		let calcColumns = false;

		const contractID = site.contract_id;
		const EVNInputs =
			site.evnInput != null
				? { ...site.evnInput, FITPrice: site.FITPrice }
				: null;

		const prodData = {
			solarON: site.solar_ON,
			solarOFF: site.solar_OFF,
			solarTotal: site.solar_total,
			contractID,
		};

		// Solar Production object
		const prodObj = solarCalc.calculateProd(prodData, EVNInputs);

		// Price object
		const priceObj = getPriceObj(site);

		// Calculations parameters
		const prod = {
			ON: prodObj.solarON,
			OFF: prodObj.solarOFF,
			TOTAL: prodObj.solarTotal,
		};
		const price = {
			ON: priceObj.price_on,
			OFF: priceObj.price_off,
			FT: site.FT_cost,
		};
		const discount = {
			ON: site.ON_discount,
			OFF: site.OFF_discount,
			FT: site.FT_discount,
		};

		// Get columns and values for each contract types
		switch (contractID) {
			case "SC":
				calcColumns = handleSCSites(site, prod, price, discount);
				break;
			case "FIT":
				calcColumns = handleFITSites(site, prod, price, discount);
				break;
			case "SCFITGY":
			case "SCFITCL":
				calcColumns = handleSCFITSites(site, prod, price, discount);
				break;

			default:
				break;
		}

		const amount_ex_VAT = calcColumns ? calcColumns.amount_ex_VAT : 0;

		// VAT calculations
		const vatPercent = site.vat;
		const currency = site.currency;
		const VATObj = solarCalc.calculateVAT(amount_ex_VAT, currency, vatPercent);
		const VATPrice = VATObj.VAT;
		const tempTotal = VATObj.totalDue;

		// Savings
		// const savingsObj = solarCalc.calculateSavings(
		// 	prod,
		// 	price,
		// 	discount,
		// 	currency
		// );
		// const savings = savingsObj.savingsTotal;
		const savings = savingData.find(saving => saving.table_name === site.table_name)?.saving || 0;

		// Irradiance
		let irr = solarCalc.calculateIrr(site.irradiance_sum).toFixed(2);

		let tableColumns = {};

		if (calcColumns) {
			// Non SCFIT sites
			if (!contractID.includes("SCFIT")) {
				tableColumns = {
					...calcColumns,
					amount_ex_VAT: formatNumber(amount_ex_VAT, 0),
					vat: formatNumber(VATPrice.toFixed(2), 0),
					amount_inc_VAT: formatNumber(tempTotal.toFixed(2), 0),
				};
			}
			// SCFIT sites - Some columns are removed due to VAT recalculations
			else {
				tableColumns = {
					...calcColumns,
				};
			}
		}

		tableColumns.entity_id = site.entity_id;
		tableColumns.company = site.company;
		tableColumns.site_code = site.site_code;
		tableColumns.site_name = site.site_name;
		tableColumns.contract_desc = site.contract_desc;
		tableColumns.proforma_inv_no = site.invoice_no ? site.invoice_no : "-";
		tableColumns.invoice_date = site.invoice_no ? site.invoice_date : "-";
		tableColumns.invoice_due_date = site.invoice_no ? moment(site.invoice_date, "YYYYMMDD").add(parseFloat(site.credit_term), "day").format("YYYY-MM-DD") : "-";
		tableColumns.project_id = site.SF_project_id;
		tableColumns.tariff_ON = site.price_on;
		tableColumns.tariff_OFF = site.price_off;
		tableColumns.savings = formatNumber(savings, 0);
		tableColumns.irr = irr;
		tableColumns.brut_PR = site.brut_PR;
		tableColumns.ambient_temp_avg = formatNumber(site.ambient_temp_avg, 2);

		return tableColumns;
	};

	/**
	 * Calculate date for SC sites
	 * @param {obj} site
	 * @param {obj} prod
	 * @param {obj} price
	 * @param {obj} discount
	 * @returns Data obj of SC sites
	 */
	const handleSCSites = (site, prod, price, discount) => {
		// Price object
		const priceObj = getPriceObj(site);

		// Unit costs
		const unit_cost_ON = solarCalc.calculateUnitCost(
			priceObj.price_on,
			site.ON_discount,
			site.currency
		);

		const unit_cost_OFF = solarCalc.calculateUnitCost(
			priceObj.price_off,
			site.OFF_discount,
			site.currency
		);

		// Costs
		const costsObj = solarCalc.calculateProdCost(
			prod,
			price,
			discount,
			site.currency
		);
		const sub_ON = costsObj.costON;
		const sub_OFF = costsObj.costOFF;
		const amount_ex_VAT = costsObj.costTotal + site.special_discount;

		const resObj = {
			discount_ON: formatNumber(parseFloat(site.ON_discount * 100), 2),
			discount_OFF: formatNumber(parseFloat(site.OFF_discount * 100), 2),
			solar_ON: formatNumber(prod.ON, 3),
			unit_cost_ON: formatNumber(unit_cost_ON, 2),
			sub_ON: formatNumber(sub_ON.toFixed(2), 0),
			solar_OFF: formatNumber(prod.OFF, 3),
			unit_cost_OFF: formatNumber(unit_cost_OFF, 2),
			sub_OFF: formatNumber(sub_OFF.toFixed(2), 0),
			solar_total: formatNumber(prod.TOTAL, 3),
			del_peak: "-",
			del_std: "-",
			del_total: "-",
			FIT_on: "-",
			amount_ex_VAT: amount_ex_VAT,
			shared_percentage: "-",
			shared_rev: "-",
		};

		return resObj;
	};

	/**
	 * Calculate date for FIT sites
	 * @param {obj} site
	 * @param {obj} prod
	 * @param {obj} price
	 * @param {obj} discount
	 * @returns Data obj of FIT sites
	 */
	const handleFITSites = (site, prod, price, discount) => {
		// Price object
		const priceObj = getPriceObj(site);

		// Unit costs
		const unit_cost = solarCalc.calculateUnitCost(
			priceObj.price_on,
			site.ON_discount,
			site.currency
		);

		// Costs
		const costsObj = solarCalc.calculateProdCost(
			prod,
			price,
			discount,
			site.currency
		);

		const amount_ex_VAT = costsObj.costTotal + site.special_discount;

		const resObj = {
			solar_ON: "-",
			unit_cost_ON: "-",
			sub_ON: "-",
			solar_OFF: "-",
			unit_cost_OFF: "-",
			sub_OFF: "-",
			solar_total: "-",
			discount_ON: "-",
			discount_OFF: "-",
			del_peak: formatNumber(prod.ON, 3),
			del_std: formatNumber(prod.OFF, 3),
			del_total: formatNumber(prod.TOTAL, 3),
			FIT_on: unit_cost,
			shared_percentage: "-",
			shared_rev: "-",
			amount_ex_VAT: amount_ex_VAT,
		};

		return resObj;
	};

	/**
	 * Calculate additional data of SCFIT sites
	 * @param {obj} site
	 * @param {string} total_ex_vat
	 * @returns Additional data Obj of SCFIT sites
	 */
	const handleSCFITSites = (site, prod, price, discount) => {
		const EVNInputs = site.evnInput;

		const FITProd = {
			ON: EVNInputs != null ? EVNInputs.EVN_on : 0,
			OFF: EVNInputs != null ? EVNInputs.EVN_off : 0,
			TOTAL: EVNInputs != null ? EVNInputs.EVN_total : 0,
		};

		// Get SC values of site
		const SCValues = handleSCSites(site, prod, price, discount);

		// Shared revenue calculations
		const shared_percentage = site.FT_discount;
		const display_percentage = formatNumber(shared_percentage * 100, 2);
		const FIT_on = site.FITOn;

		const SRObj = solarCalc.calculateSharedRevenue(
			site.contract_id,
			shared_percentage,
			FITProd.TOTAL,
			FIT_on
		);

		// Recalculate subtotal and VAT with shared revenue
		const shared_rev = formatNumber(SRObj.subtotal, 0);
		const clean_TEV = SCValues.amount_ex_VAT;
		const clean_SR = shared_rev.replaceAll(",", "");

		const updated_AEV = solarCalc.parseFormatNumber(
			parseFloat(clean_TEV) + parseFloat(clean_SR),
			0
		);

		const vatPercent = site.vat;
		const currency = site.currency;

		const new_vat_obj = solarCalc.calculateVAT(
			updated_AEV,
			currency,
			vatPercent
		);
		const updated_VAT = formatNumber(new_vat_obj.VAT, 0);
		const updated_AIV = formatNumber(new_vat_obj.totalDue, 0);

		const subtotal = site.special_discount ? updated_AEV + site.special_discount : updated_AEV;

		return {
			solar_ON: SCValues.solar_ON,
			unit_cost_ON: SCValues.unit_cost_ON,
			sub_ON: SCValues.sub_ON,
			solar_OFF: SCValues.solar_OFF,
			unit_cost_OFF: SCValues.unit_cost_OFF,
			sub_OFF: SCValues.sub_OFF,
			solar_total: SCValues.solar_total,
			del_peak: formatNumber(FITProd.ON, 2),
			del_std: formatNumber(FITProd.OFF, 2),
			del_total: formatNumber(FITProd.TOTAL, 2),
			FIT_on: site.FITOn,
			shared_percentage: display_percentage,
			shared_rev,
			amount_ex_VAT: formatNumber(subtotal, 0),
			vat: updated_VAT,
			discount_ON: formatNumber(parseFloat(site.ON_discount * 100), 2),
			discount_OFF: formatNumber(parseFloat(site.OFF_discount * 100), 2),
			amount_inc_VAT: updated_AIV,
		};
	};

	/**
	 * Get price object based on contract ID
	 * @param {obj} data
	 * @returns price object
	 */
	const getPriceObj = (data) => {
		return data.contract_id === "FIT"
			? { price_on: data.FITOn, price_off: data.FITOff }
			: { price_on: data.price_on, price_off: data.price_off };
	};

	/**
	 * Show under-review notifications
	 */
	const alertMsg = () => {
		Noti.create({
			type: "w",
			msg: "Invoices of the group are still under review",
		});
	};

	useEffect(() => {
		const KCToken = user.sub;
		GYAxios.get(`/users/filter?column=keycloak_id&value=${KCToken}`).then(
			(res) => {
				const userInfo =
					res.data.error == null && res.data.length > 0 ? res.data[0] : false;

				if (userInfo) {
					setSiteSet(userInfo.country);
					setCountry(userInfo.country);
				}
			}
		);
	}, []);

	useEffect(() => {
		const param = {
			date: moment(selectedMonth.format("YYYY-MM-DD")).endOf("month"),
			viewStatus: props.viewLevel,
		};
		const MK1SitesReq = GYAxios.post("/reports/summary_groups", {
			...param,
			group: "MK1",
		});
		const MK2SitesReq = GYAxios.post("/reports/summary_groups", {
			...param,
			group: "MK2",
		});
		const CPALLSitesReq = GYAxios.post("/reports/summary_groups", {
			...param,
			group: "CP",
		});
		Axios.all([MK1SitesReq, MK2SitesReq, CPALLSitesReq]).then(
			Axios.spread((...responses) => {
				const formattedRes = responses.map((res) => res.data);
				let disabledArr = [];

				formattedRes.map(
					(group) => !group.isApproved && disabledArr.push(group.name)
				);

				setIsDisabled(disabledArr);
			})
		);
	}, [selectedMonth]);

	useEffect(() => {
		if (!country) { return }
		if (!savingData?.length) { return }

		setIsReady(false);
		fetchData(siteSet);

		const newYearMonth = selectedMonth.format("MMMM YYYY");
		const newYear = selectedMonth.format("YYYY");

		if (newYearMonth !== yearMonth) setYearMonth(yearMonth);
		if (newYear !== selectedYear) setSelectedYear(newYear);
	}, [siteSet, peakLabels, selectedMonth, country, savingData]);

	return (
		<>
			<div className="perf-table panel panel-default">
				<div className="panel-heading ">
					<Row>
						<Col md="12" style={{ marginBottom: "10px" }}>
							<h2 className="panel-title" id="date">
								Summary table - Revenue Report
							</h2>
						</Col>
					</Row>
					<Row>
						<Col md="6">
							<div className="month-selector">
								Choose another Month: &nbsp;&nbsp;
								<DatePicker
									minDate={localTimeToPickerTime(minDate)}
									maxDate={localTimeToPickerTime(maxDate)}
									selected={localTimeToPickerTime(selectedMonth.toDate())}
									onChange={(date) => {
										setSelectedMonth(moment(pickerTimeTolocalTime(date)));
									}}
									dateFormat="MMM yyyy"
									showMonthYearPicker
								/>
							</div>
						</Col>
						<Col md="6" style={{ textAlign: "right" }}>
							<div id="summary-btns">
								<button
									className={"btn btn-primary"}
									onClick={() => {
										if (siteSet !== "TH") {
											setSiteSet("TH");
											setCountry("TH");
										}
									}}
								>
									Thailand
								</button>
								<button
									className={"btn btn-primary"}
									onClick={() => {
										if (siteSet !== "VN") {
											setSiteSet("VN");
											setCountry("VN");
										}
									}}
								>
									Vietnam
								</button>
								<button
									className={"btn btn-primary"}
									onClick={() => {
										if (siteSet !== "PH") {
											setSiteSet("PH");
											setCountry("PH");
										}
									}}
								>
									Philippines
								</button>
								{isDisabled != null && (
									<>
										<button
											className={
												!isDisabled.includes("MK1")
													? "btn btn-primary"
													: "btn btn-primary disabled"
											}
											onClick={() => {
												if (siteSet !== "MK1" && !isDisabled.includes("MK1")) {
													setSiteSet("MK1");
													setCountry("TH");
												}

												if (isDisabled.includes("MK1")) alertMsg();
											}}
										>
											Makro P1
										</button>
										<button
											className={
												!isDisabled.includes("MK2")
													? "btn btn-primary"
													: "btn btn-primary disabled"
											}
											onClick={() => {
												if (siteSet !== "MK2" && !isDisabled.includes("MK2")) {
													setSiteSet("MK2");
													setCountry("TH");
												}

												if (isDisabled.includes("MK2")) alertMsg();
											}}
										>
											Makro P2
										</button>
										<button
											className={
												!isDisabled.includes("CP")
													? "btn btn-primary"
													: "btn btn-primary disabled"
											}
											onClick={() => {
												if (siteSet !== "CP" && !isDisabled.includes("CP")) {
													setSiteSet("CP");
													setCountry("TH");
												}

												if (isDisabled.includes("CP")) alertMsg();
											}}
										>
											CP All
										</button>
									</>
								)}
							</div>
						</Col>
					</Row>
				</div>
				<div className="panel-body">
					<MaterialTable
						columns={columns}
						data={rows}
						isLoading={!isReady}
						options={{
							showTitle: false,
							exportButton: true,
							exportAllData: true,
							grouping: true,
							pageSize: 15,
							pageSizeOptions: [15, 20, 40, 60],
							headerStyle: {
								fontSize: "1rem",
								textAlign: "center",
							},
							cellStyle: {
								textAlign: "center",
							},
							fixedColumns: {
								left: 2,
							},
							exportFileName: `SummRepRev-${yearMonth}-${siteSet}`,
						}}
					/>
				</div>
			</div>
			<Noti />
		</>
	);
}

export default SummaryReport;